import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';

export interface SeoMeta {
  title: string;
  description?: string;
  image?: string;
  type?: string; // og:type, e.g. 'website' | 'product' | 'article'
}

/**
 * Centralizes page <title>, meta description, Open Graph tags, and JSON-LD
 * structured-data injection. JSON-LD blocks are keyed by id so a page can
 * replace its own block and clear it on leave (see clearJsonLd).
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private renderer: Renderer2;
  private readonly suffix = 'Maven Merge';

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private doc: Document,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  setMeta(opts: SeoMeta): void {
    this.title.setTitle(`${opts.title} | ${this.suffix}`);
    this.meta.updateTag({ property: 'og:title', content: opts.title });
    this.meta.updateTag({ property: 'og:type', content: opts.type || 'website' });
    if (opts.description) {
      this.meta.updateTag({ name: 'description', content: opts.description });
      this.meta.updateTag({ property: 'og:description', content: opts.description });
    }
    if (opts.image) this.meta.updateTag({ property: 'og:image', content: opts.image });
  }

  /** Sets the robots meta (e.g. 'noindex, follow' for 404s). Pass 'index, follow' to reset. */
  setRobots(content: string): void {
    this.meta.updateTag({ name: 'robots', content });
  }

  /** Current absolute page URL (client-side); safe-guarded for non-browser. */
  currentUrl(): string {
    return typeof location !== 'undefined' ? location.href : '';
  }

  /** Insert or replace a JSON-LD <script> in <head>, identified by id.
      Escapes '<' as < so CMS-entered text can't break out of the
      <script> tag (e.g. a literal "</script>" inside a name). */
  setJsonLd(id: string, data: unknown): void {
    this.clearJsonLd(id);
    const script = this.renderer.createElement('script') as HTMLScriptElement;
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(data).replace(/</g, '\\u003c');
    this.renderer.appendChild(this.doc.head, script);
  }

  clearJsonLd(id: string): void {
    const existing = this.doc.getElementById(id);
    if (existing) existing.remove();
  }
}
